/**
 * @description Time Clock Settings panel for break allowances, clock rules, and labor law compliance
 *
 * Key features:
 * - Break allowances editor (name, duration, count, paid/unpaid)
 * - Role-level override management
 * - Auto clock-out settings
 * - PIN requirement toggle
 * - Labor law compliance notification (flags states with special requirements)
 * - Custom overtime/double-time/7th day rule configuration
 *
 * ENTERPRISE NOTES:
 * - Time Clock settings are DEFAULT ENABLED for all merchants
 * - Break allowances support role-level overrides
 * - Draft state managed by parent GlobalSettingsPage
 * - FLSA (Federal) is the default - employers configure their own rules
 * - We NOTIFY about compliance states but do NOT preconfigure or recommend settings
 * - This approach minimizes LynxPay's liability for labor law accuracy
 *
 * @dependencies
 * - Parent provides getValue/updateDraft for draft state management
 *
 * @see .claude/tasks/Global_Settings_Architecture.md
 */

import React from 'react';
import { Clock, Plus, Trash2, Users, ChevronDown, Info, Scale, AlertTriangle, MapPin, X, ShieldCheck, Check } from 'lucide-react';
import { Disclosure, Switch, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const TimeClockSettingsPanel = ({
  settings,
  getValue,
  updateDraft,
  saving,
  onRoleOverrideUpdate,
  onRoleOverrideDelete,
  companyState, // Company's state from company data (e.g., "CA", "CO", "TX")
  selectedLocation, // Currently selected location (for multi-location companies)
  hasMultipleLocations // Whether company has 2+ locations
}) => {
  // Use location state if available, otherwise fall back to company state
  const effectiveState = selectedLocation?.state || companyState;

  // Find specific settings
  const breakAllowances = settings.find(s => s.key === 'time_clock.break_allowances');
  const autoClockOut = settings.find(s => s.key === 'time_clock.auto_clock_out');
  const requirePin = settings.find(s => s.key === 'time_clock.require_pin');
  const earlyClockIn = settings.find(s => s.key === 'time_clock.allow_early_clock_in');
  const allowUnscheduled = settings.find(s => s.key === 'time_clock.allow_unscheduled_clock_in');
  const overtimeThreshold = settings.find(s => s.key === 'time_clock.overtime_threshold');

  // Labor law compliance settings (from migration 114)
  const laborLawCompliance = settings.find(s => s.key === 'time_clock.labor_law_compliance');

  // States with special labor law requirements (for notification purposes only)
  // We DO NOT preconfigure or recommend settings - just notify employer to review
  // Each state has a specific notice describing their requirements
  const COMPLIANCE_STATES = {
    'CA': { name: 'California', notice: 'California has specific daily overtime and meal/rest break requirements.' },
    'California': { name: 'California', notice: 'California has specific daily overtime and meal/rest break requirements.' },
    'CO': { name: 'Colorado', notice: 'Colorado has specific daily overtime requirements.' },
    'Colorado': { name: 'Colorado', notice: 'Colorado has specific daily overtime requirements.' },
    'AK': { name: 'Alaska', notice: 'Alaska has specific daily overtime requirements.' },
    'Alaska': { name: 'Alaska', notice: 'Alaska has specific daily overtime requirements.' },
    'NV': { name: 'Nevada', notice: 'Nevada has specific daily overtime requirements.' },
    'Nevada': { name: 'Nevada', notice: 'Nevada has specific daily overtime requirements.' },
    'OR': { name: 'Oregon', notice: 'Oregon has specific overtime and meal/rest break requirements.' },
    'Oregon': { name: 'Oregon', notice: 'Oregon has specific overtime and meal/rest break requirements.' },
    'WA': { name: 'Washington', notice: 'Washington has specific overtime and meal/rest break requirements.' },
    'Washington': { name: 'Washington', notice: 'Washington has specific overtime and meal/rest break requirements.' },
    'KY': { name: 'Kentucky', notice: 'Kentucky has specific daily overtime and meal/rest break requirements.' },
    'Kentucky': { name: 'Kentucky', notice: 'Kentucky has specific daily overtime and meal/rest break requirements.' },
    'NY': { name: 'New York', notice: 'New York has specific overtime and meal/rest break requirements.' },
    'New York': { name: 'New York', notice: 'New York has specific overtime and meal/rest break requirements.' },
    'RI': { name: 'Rhode Island', notice: 'Rhode Island has specific overtime and meal/rest break requirements.' },
    'Rhode Island': { name: 'Rhode Island', notice: 'Rhode Island has specific overtime and meal/rest break requirements.' }
  };

  // Check if current location/company is in a state with special labor requirements
  const complianceState = effectiveState ? COMPLIANCE_STATES[effectiveState] : null;
  const hasComplianceRequirements = !!complianceState;

  const handleToggleUpdate = (key, currentValue) => {
    if (typeof currentValue === 'boolean') {
      updateDraft(key, !currentValue);
    } else if (typeof currentValue === 'object' && 'enabled' in currentValue) {
      updateDraft(key, { ...currentValue, enabled: !currentValue.enabled });
    }
  };

  const handleObjectFieldUpdate = (key, field, value) => {
    const current = getValue(key) || {};
    updateDraft(key, { ...current, [field]: value });
  };

  // Helper for rendering a modern Switch
  const ModernSwitch = ({ checked, onChange, disabled, label }) => (
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
        checked ? 'bg-brand' : 'bg-gray-200'
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </Switch>
  );

  // Render break allowances editor
  const renderBreakAllowancesEditor = () => {
    if (!breakAllowances) return null;

    const breaks = getValue('time_clock.break_allowances') || [];

    const addBreak = () => {
      const breakNumber = breaks.length + 1;
      const defaultName = breakNumber === 1 ? 'Lunch' : `Break ${breakNumber}`;
      const newBreaks = [
        ...breaks,
        { name: defaultName, durationMinutes: 15, countAllowed: 1, isPaid: false }
      ];
      updateDraft('time_clock.break_allowances', newBreaks);
    };

    const updateBreak = (index, field, value) => {
      const updated = [...breaks];
      updated[index] = { ...updated[index], [field]: value };
      updateDraft('time_clock.break_allowances', updated);
    };

    const removeBreak = (index) => {
      const newBreaks = breaks.filter((_, i) => i !== index);
      updateDraft('time_clock.break_allowances', newBreaks);
    };

    return (
      <Disclosure>
        {({ open }) => (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md">
            <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-yellow-50 rounded-xl text-yellow-600">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{breakAllowances.displayName}</h3>
                  <p className="text-sm text-gray-500 font-medium">
                    {breaks.length === 0
                      ? 'No break types configured'
                      : `${breaks.length} break type${breaks.length !== 1 ? 's' : ''} configured`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {breakAllowances.valueSource !== 'default' && (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
                    Customized
                  </span>
                )}
                <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
              </div>
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">{breakAllowances.description}</p>

                <div className="space-y-4 mb-6">
                  {breaks.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                        <Clock className="w-6 h-6 text-gray-300" />
                      </div>
                      <p className="text-gray-900 font-semibold text-sm">No break types</p>
                      <p className="text-gray-500 text-xs mt-1">Employees can log breaks without restrictions</p>
                    </div>
                  ) : (
                    breaks.map((brk, index) => (
                      <div key={index} className="group flex flex-wrap sm:flex-nowrap items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 transition-colors hover:bg-gray-100/50">
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Name</label>
                          <input
                            type="text"
                            value={brk.name}
                            onChange={(e) => updateBreak(index, 'name', e.target.value)}
                            placeholder="Lunch, Coffee Break..."
                            className="w-full px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                          />
                        </div>
                        <div className="w-24">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Duration</label>
                          <div className="relative">
                            <input
                              type="number"
                              value={brk.durationMinutes}
                              onChange={(e) => updateBreak(index, 'durationMinutes', parseInt(e.target.value) || 0)}
                              className="w-full pl-3 pr-8 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">m</span>
                          </div>
                        </div>
                        <div className="w-24">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Limit</label>
                          <div className="relative">
                            <input
                              type="number"
                              value={brk.countAllowed}
                              onChange={(e) => updateBreak(index, 'countAllowed', parseInt(e.target.value) || 1)}
                              className="w-full pl-3 pr-8 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">x</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Paid</label>
                          <Switch
                            checked={brk.isPaid}
                            onChange={(checked) => updateBreak(index, 'isPaid', checked)}
                            className={cn(
                              'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                              brk.isPaid ? 'bg-green-500' : 'bg-gray-300'
                            )}
                          >
                            <span className={cn(
                              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out',
                              brk.isPaid ? 'translate-x-4' : 'translate-x-0'
                            )} />
                          </Switch>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBreak(index)}
                          className="mt-4 sm:mt-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={addBreak}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-yellow-600 hover:bg-yellow-50 rounded-xl transition-all disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add Break Type
                  </button>
                </div>

                {/* Role Overrides Section */}
                {breakAllowances.allowRoleOverride && breakAllowances.roleOverrides?.length > 0 && (
                  <div className="mt-8 p-4 bg-purple-50/50 rounded-2xl border border-purple-100">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-1.5 bg-purple-100 rounded-lg">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-bold text-purple-900">Role-Specific Overrides</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {breakAllowances.roleOverrides.map(override => (
                        <div key={override.roleId} className="flex items-center justify-between p-3 bg-white rounded-xl border border-purple-100 shadow-sm">
                          <div>
                            <p className="text-xs font-bold text-purple-900">{override.roleName}</p>
                            <p className="text-[10px] text-purple-500 font-medium">{override.value?.length || 0} custom breaks</p>
                          </div>
                          <button
                            onClick={() => onRoleOverrideDelete(override.roleId, 'time_clock.break_allowances')}
                            className="p-1.5 text-purple-300 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  // Render labor law compliance section (collapsible)
  // Note: We only show configuration options - NO state presets or recommendations
  // Employers configure their own rules based on their legal counsel's advice
  const renderLaborLawComplianceSection = () => {
    if (!laborLawCompliance) return null;

    const dailyOTEnabled = getValue('time_clock.overtime_daily_enabled') || false;
    const dailyOTThreshold = getValue('time_clock.overtime_daily_threshold') || 8;
    const doubleTimeOn = getValue('time_clock.double_time_enabled') || false;
    const doubleTimeThresh = getValue('time_clock.double_time_threshold') || 12;
    const doubleTimeMult = getValue('time_clock.double_time_multiplier') || 2.0;
    const seventhDayOn = getValue('time_clock.seventh_day_rule_enabled') || false;
    const seventhDayFirst8 = getValue('time_clock.seventh_day_first_eight_rate') || 1.5;
    const seventhDayAfter8 = getValue('time_clock.seventh_day_after_eight_rate') || 2.0;

    // Check if any non-federal rules are enabled
    const hasCustomRules = dailyOTEnabled || doubleTimeOn || seventhDayOn;

    // Count enabled rules for summary
    const enabledRulesCount = [dailyOTEnabled, doubleTimeOn, seventhDayOn].filter(Boolean).length;

    return (
      <Disclosure>
        {({ open }) => (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md">
            <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-orange-50 rounded-xl text-orange-600">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Overtime & Labor Rules</h3>
                  <p className="text-sm text-gray-500 font-medium">
                    {enabledRulesCount === 0
                      ? 'Federal (FLSA) defaults only'
                      : `${enabledRulesCount} custom rule${enabledRulesCount !== 1 ? 's' : ''} enabled`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {hasCustomRules && (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-orange-100 text-orange-700 px-2 py-1 rounded-md">
                    Customized
                  </span>
                )}
                <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
              </div>
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4 text-gray-500" />
                      <p className="text-xs font-bold text-gray-700">Default: Federal (FLSA)</p>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      By default, only weekly overtime at 40 hours is tracked. Enable additional rules below if required by your state or local labor laws.
                    </p>
                  </div>

                  <div className="bg-blue-50/50 rounded-2xl p-4 border border-blue-100 flex gap-3">
                    <div className="p-2 bg-blue-100 rounded-xl h-fit">
                      <AlertTriangle className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-blue-900 mb-1">Your Responsibility</p>
                      <p className="text-xs text-blue-700 leading-relaxed">
                        Consult with legal counsel to determine which rules apply to your business.
                      </p>
                    </div>
                  </div>
                </div>

                {/* OT configuration cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Daily OT Card */}
                  <div className={cn(
                    'p-4 rounded-2xl border transition-all duration-200',
                    dailyOTEnabled ? 'bg-white border-yellow-200 shadow-sm' : 'bg-gray-100/50 border-gray-200'
                  )}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-bold text-gray-900">Daily Overtime</span>
                      <ModernSwitch
                        checked={dailyOTEnabled}
                        onChange={(checked) => updateDraft('time_clock.overtime_daily_enabled', checked)}
                        disabled={false}
                        label="Daily Overtime"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 mb-3">OT after X hours in a single day</p>
                    <div className="flex items-center gap-2 mt-auto">
                      <span className="text-[10px] font-bold text-gray-400 uppercase">Threshold</span>
                      <div className="relative flex-1">
                        <input
                          type="number"
                          value={dailyOTThreshold}
                          onChange={(e) => updateDraft('time_clock.overtime_daily_threshold', parseInt(e.target.value) || 8)}
                          disabled={!dailyOTEnabled}
                          className="w-full px-2 py-1 text-xs font-bold bg-transparent border-b border-gray-200 focus:border-brand outline-none transition-colors disabled:opacity-50"
                        />
                        <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">hrs</span>
                      </div>
                    </div>
                  </div>

                  {/* Double-Time Card */}
                  <div className={cn(
                    'p-4 rounded-2xl border transition-all duration-200',
                    doubleTimeOn ? 'bg-white border-yellow-200 shadow-sm' : 'bg-gray-100/50 border-gray-200'
                  )}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-bold text-gray-900">Double-Time</span>
                      <ModernSwitch
                        checked={doubleTimeOn}
                        onChange={(checked) => updateDraft('time_clock.double_time_enabled', checked)}
                        disabled={false}
                        label="Double Time"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 mb-3">2x pay after X hours in a day</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase w-12">After</span>
                        <div className="relative flex-1">
                          <input
                            type="number"
                            value={doubleTimeThresh}
                            onChange={(e) => updateDraft('time_clock.double_time_threshold', parseInt(e.target.value) || 12)}
                            disabled={!doubleTimeOn}
                            className="w-full px-2 py-1 text-xs font-bold bg-transparent border-b border-gray-200 focus:border-brand outline-none transition-colors disabled:opacity-50"
                          />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">hrs</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase w-12">Rate</span>
                        <div className="relative flex-1">
                          <input
                            type="number"
                            value={doubleTimeMult}
                            onChange={(e) => updateDraft('time_clock.double_time_multiplier', parseFloat(e.target.value) || 2.0)}
                            disabled={!doubleTimeOn}
                            step="0.1"
                            className="w-full px-2 py-1 text-xs font-bold bg-transparent border-b border-gray-200 focus:border-brand outline-none transition-colors disabled:opacity-50"
                          />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">x</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 7th Day Card */}
                  <div className={cn(
                    'p-4 rounded-2xl border transition-all duration-200',
                    seventhDayOn ? 'bg-white border-yellow-200 shadow-sm' : 'bg-gray-100/50 border-gray-200'
                  )}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-bold text-gray-900">7th Day Rule</span>
                      <ModernSwitch
                        checked={seventhDayOn}
                        onChange={(checked) => updateDraft('time_clock.seventh_day_rule_enabled', checked)}
                        disabled={false}
                        label="7th Day Rule"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 mb-3">OT on 7th consecutive work day</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase w-12">First 8</span>
                        <div className="relative flex-1">
                          <input
                            type="number"
                            value={seventhDayFirst8}
                            onChange={(e) => updateDraft('time_clock.seventh_day_first_eight_rate', parseFloat(e.target.value) || 1.5)}
                            disabled={!seventhDayOn}
                            className="w-full px-2 py-1 text-xs font-bold bg-transparent border-b border-gray-200 focus:border-brand outline-none transition-colors disabled:opacity-50"
                          />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">x</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase w-12">After 8</span>
                        <div className="relative flex-1">
                          <input
                            type="number"
                            value={seventhDayAfter8}
                            onChange={(e) => updateDraft('time_clock.seventh_day_after_eight_rate', parseFloat(e.target.value) || 2.0)}
                            disabled={!seventhDayOn}
                            className="w-full px-2 py-1 text-xs font-bold bg-transparent border-b border-gray-200 focus:border-brand outline-none transition-colors disabled:opacity-50"
                          />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">x</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status indicator */}
                {hasCustomRules && (
                  <div className="mt-4 flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-100 rounded-xl">
                    <Info className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                    <p className="text-xs text-yellow-800">
                      <strong>Custom rules enabled.</strong> These settings will be applied when calculating overtime for this {hasMultipleLocations ? 'location' : 'company'}.
                    </p>
                  </div>
                )}
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  // Render modern toggle setting card
  const renderToggleSetting = (setting, icon) => {
    if (!setting) return null;

    const Icon = icon;
    const value = getValue(setting.key);
    const isEnabled = typeof value === 'boolean' ? value : value?.enabled;

    return (
      <div className="group bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-300">
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-gray-50 rounded-xl text-gray-600 transition-colors group-hover:bg-yellow-50 group-hover:text-yellow-600">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{setting.displayName}</h3>
            <p className="text-sm text-gray-500 font-medium">{setting.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {setting.valueSource !== 'default' && (
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
              Customized
            </span>
          )}
          <ModernSwitch
            checked={isEnabled}
            onChange={() => handleToggleUpdate(setting.key, value)}
            disabled={!setting.canEdit}
            label={setting.displayName}
          />
        </div>
      </div>
    );
  };

  // Render modern object setting card
  const renderObjectSetting = (setting, icon, configFields) => {
    if (!setting) return null;

    const Icon = icon;
    const value = getValue(setting.key) || {};
    const isEnabled = value.enabled;

    return (
      <Disclosure>
        {({ open }) => (
          <div className={cn(
            'bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm hover:shadow-md',
            open ? 'border-yellow-200 ring-1 ring-yellow-100' : 'border-gray-200'
          )}>
            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn(
                  'p-2.5 rounded-xl transition-colors',
                  isEnabled ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-50 text-gray-400'
                )}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{setting.displayName}</h3>
                  <p className="text-sm text-gray-500 font-medium">{setting.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {setting.valueSource !== 'default' && (
                  <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
                    Customized
                  </span>
                )}
                <ModernSwitch
                  checked={isEnabled}
                  onChange={() => handleToggleUpdate(setting.key, value)}
                  disabled={!setting.canEdit}
                  label={setting.displayName}
                />
                {configFields && (
                  <Disclosure.Button className="p-1 text-gray-400 hover:text-gray-600 outline-none">
                    <ChevronDown className={cn('w-5 h-5 transition-transform duration-200', open && 'rotate-180')} />
                  </Disclosure.Button>
                )}
              </div>
            </div>

            <Transition
              show={open && isEnabled}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {configFields.map(field => (
                    <div key={field.key} className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">{field.label}</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={value[field.key] ?? field.default}
                          onChange={(e) => handleObjectFieldUpdate(setting.key, field.key, parseInt(e.target.value) || field.default)}
                          min={field.min}
                          max={field.max}
                          className="w-full pl-3 pr-12 py-2 text-sm font-bold bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">{field.suffix}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Location Context Banner - shown when multi-location */}
      {hasMultipleLocations && selectedLocation && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-blue-900">
              Configuring settings for <span className="font-semibold">{selectedLocation.name}</span>
              {selectedLocation.state && (
                <span className="text-blue-600 ml-1">({selectedLocation.state})</span>
              )}
            </p>
            {hasComplianceRequirements ? (
              <p className="text-xs text-amber-700 font-medium mt-1">
                {complianceState.notice}
              </p>
            ) : (
              <p className="text-xs text-blue-700 mt-1">
                Labor law rules are determined by location state.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Single-location compliance notice - only shown for single-location companies in compliance states */}
      {!hasMultipleLocations && hasComplianceRequirements && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            {complianceState.notice}
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-600 rounded-2xl p-6 shadow-xl shadow-blue-100 text-white flex flex-col md:flex-row items-center gap-6">
        <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl shrink-0">
          <ShieldCheck className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h4 className="text-lg font-bold mb-1">Time Clock Compliance</h4>
          <p className="text-sm text-blue-100 leading-relaxed">
            Configure break allowances, overtime thresholds, and regional labor laws.
            These settings help ensure your business stays compliant and your payroll is accurate.
          </p>
        </div>
        <div className="h-12 w-px bg-white/20 hidden md:block" />
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Regional OT</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Paid Breaks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">PIN Security</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Auto-Clockout</span>
          </div>
        </div>
      </div>

      {/* Labor Law Compliance - only show if setting exists */}
      {laborLawCompliance && (
        <section>
          <div className="flex items-center gap-2 mb-4 ml-1">
            <Scale className="w-4 h-4 text-gray-400" />
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Regional Compliance</h3>
          </div>
          {renderLaborLawComplianceSection()}
        </section>
      )}

      {/* Break Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4 ml-1">
          <Clock className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Break Configuration</h3>
        </div>
        <div className="space-y-4">
          {renderBreakAllowancesEditor()}
          
          <Disclosure>
            {({ open }) => (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md">
                <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-gray-50 rounded-xl text-gray-600">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">Break Frequency Mode</h3>
                      <p className="text-sm text-gray-500 font-medium">
                        {(getValue('time_clock.break_frequency_mode') || 'per_shift') === 'per_shift'
                          ? 'Per Shift - Full allowance at shift start'
                          : `Hours Worked - Every ${getValue('time_clock.break_hours_threshold') || 4} hours`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getValue('time_clock.break_frequency_mode') === 'hours_worked' && (
                      <span className="text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
                        Customized
                      </span>
                    )}
                    <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
                  </div>
                </Disclosure.Button>

                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Disclosure.Panel className="px-5 pb-5 pt-0">
                    <div className="h-px bg-gray-100 mb-5" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        onClick={() => updateDraft('time_clock.break_frequency_mode', 'per_shift')}
                        className={cn(
                          'flex flex-col p-4 rounded-xl border text-left transition-all duration-200 outline-none',
                          (getValue('time_clock.break_frequency_mode') || 'per_shift') === 'per_shift'
                            ? 'bg-yellow-50 border-yellow-200 ring-1 ring-yellow-100'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-gray-900">Per Shift</span>
                          {(getValue('time_clock.break_frequency_mode') || 'per_shift') === 'per_shift' && (
                            <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-gray-900" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Full allowance available at start of each shift. Simple and predictable.
                        </p>
                      </button>

                      <button
                        onClick={() => updateDraft('time_clock.break_frequency_mode', 'hours_worked')}
                        className={cn(
                          'flex flex-col p-4 rounded-xl border text-left transition-all duration-200 outline-none',
                          getValue('time_clock.break_frequency_mode') === 'hours_worked'
                            ? 'bg-yellow-50 border-yellow-200 ring-1 ring-yellow-100'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-gray-900">Hours Worked</span>
                          {getValue('time_clock.break_frequency_mode') === 'hours_worked' && (
                            <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-gray-900" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Breaks unlock progressively as hours are worked. Best for variable shifts.
                        </p>
                      </button>
                    </div>

                    {/* Break Hours Threshold - only shown when "Hours Worked" is selected */}
                    {getValue('time_clock.break_frequency_mode') === 'hours_worked' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-bold text-gray-900">Break Every</p>
                            <p className="text-xs text-gray-500 mt-0.5">Hours worked before a break is allowed</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={getValue('time_clock.break_hours_threshold') || 4}
                              onChange={(e) => updateDraft('time_clock.break_hours_threshold', parseInt(e.target.value) || 4)}
                              min={1}
                              max={12}
                              className="w-16 px-3 py-2 text-sm font-bold text-center bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                            />
                            <span className="text-sm font-medium text-gray-500">hours</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </Disclosure.Panel>
                </Transition>
              </div>
            )}
          </Disclosure>
        </div>
      </section>

      {/* Clock-In/Out Rules */}
      <section>
        <div className="flex items-center gap-2 mb-4 ml-1">
          <ShieldCheck className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Security & Limits</h3>
        </div>
        <div className="space-y-4">
          {renderToggleSetting(requirePin, Clock)}

          {renderToggleSetting(allowUnscheduled, Clock)}

          {renderObjectSetting(autoClockOut, Clock, [
            { key: 'maxHours', label: 'Maximum hours', default: 12, min: 1, max: 24, suffix: 'hrs' }
          ])}

          {renderObjectSetting(earlyClockIn, Clock, [
            { key: 'maxMinutesBefore', label: 'Early clock-in', default: 15, min: 0, max: 120, suffix: 'min' }
          ])}

          {renderObjectSetting(overtimeThreshold, Clock, [
            { key: 'weeklyHours', label: 'Weekly threshold', default: 40, min: 1, max: 168, suffix: 'hrs/wk' }
          ])}
        </div>
      </section>
    </div>
  );
};

export default TimeClockSettingsPanel;
